import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X } from 'lucide-react';
import { billingAPI } from '../../services/billing';
import toast from 'react-hot-toast';

const STRIPE_PUBLISHABLE_KEY = String(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '').trim();
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

function isPlaceholderKey(key) {
  return key.startsWith('pk_test_replace');
}

function Inner({ onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {},
      redirect: 'if_required',
    });

    if (error) {
      toast.error(error.message || 'Failed to add payment method');
      setLoading(false);
      return;
    }

    toast.success('Payment method added');
    onDone();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-3 border rounded">
        <PaymentElement />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={loading || !stripe} className="btn-primary">
          {loading ? 'Saving...' : 'Add Card'}
        </button>
      </div>
    </form>
  );
}

export default function AddPaymentMethodModal({ onClose }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initError, setInitError] = useState('');

  // check env key at runtime
  useEffect(() => {
    if (isPlaceholderKey(STRIPE_PUBLISHABLE_KEY)) {
      setInitError('Stripe publishable key is not set in frontend/.env (currently placeholder)');
    }
  }, []);

  const open = async () => {
    if (!stripePromise || isPlaceholderKey(STRIPE_PUBLISHABLE_KEY)) {
      setInitError('Stripe is not configured. Replace the placeholder VITE_STRIPE_PUBLISHABLE_KEY in frontend/.env with a real key and restart the frontend.');
      return;
    }

    try {
      setLoading(true);
      const { data } = await billingAPI.createSetupIntent();
      setClientSecret(data.clientSecret);
    } catch (err) {
      console.warn(err);
      const message = err?.response?.data?.error || err?.response?.data?.details || 'Could not create setup intent';
      setInitError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!clientSecret && !loading && !initError) open();

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-medium">Add payment method</h3>
          <button onClick={onClose} className="text-gray-500"><X size={18} /></button>
        </div>

        {initError ? (
          <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {initError}
          </div>
        ) : !clientSecret ? (
          <div className="text-center py-8">Preparing payment form...</div>
        ) : (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              // encourage ACH/bank account first, then card
              paymentMethodOrder: ['us_bank_account', 'card'],
            }}
          >
            <Inner onDone={onClose} />
          </Elements>
        )}
      </div>
    </div>
  );
}
